import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Edit2, Trash2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDate, generateId } from '../../utils/helpers';

const emptyForm = { firstName:'', lastName:'', dob:'', gender:'boy', bloodGroup:'A+', phone:'', email:'', address:'', classId:'', sectionId:'A', rollNumber:'', admissionDate: new Date().toISOString().split('T')[0], status:'active', parentId:'' };

function StudentList() {
  const { students, classes, parents, feeStructures, feePayments, attendance, marks, exams, academicYear, saveStudent, deleteStudent } = useApp();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isTeacher = currentUser?.role === 'teacher';

  // Classes assigned to this teacher (as class teacher)
  const teacherClasses = isTeacher
    ? classes.filter(c => c.classTeacherId === currentUser?.profileId)
    : classes;

  // Base student pool scoped to teacher's classes
  const baseStudents = isTeacher
    ? students.filter(s => teacherClasses.some(c => c.id === s.classId))
    : students;

  const [modal, setModal] = useState(null); // 'add' | 'view'
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);
  const [filterClass, setFilterClass] = useState('all');

  const openAdd = () => { setForm({...emptyForm, admissionNumber: `ADM${Date.now()}`}); setModal('add'); };
  const openView = (s) => { setSelected(s); setModal('view'); };

  const save = async () => {
    await saveStudent({ ...form, id: generateId('STU') });
    setModal(null);
  };

  const remove = async (id) => {
    if (window.confirm('Delete this student?')) await deleteStudent(id);
  };

  const getClass = (s) => { const c = classes.find(c => c.id === s.classId); return c ? `${c.name}-${s.sectionId}` : '-'; };
  const getParent = (s) => { const p = parents.find(p => p.id === s.parentId); return p ? p.name : '-'; };

  const getAttendanceStats = (studentId) => {
    const recs = attendance.filter(a => a.entityType === 'student' && a.entityId === studentId);
    const total = recs.length;
    const present = recs.filter(a => a.status === 'present' || a.status === 'late').length;
    const pct = total > 0 ? Math.round((present / total) * 100) : null;
    return { total, present, absent: total - present, pct };
  };

  const getExamPerformance = (studentId) => {
    const studentMarks = marks.filter(m => m.studentId === studentId);
    if (studentMarks.length === 0) return null;
    const totalObtained = studentMarks.reduce((sum, m) => sum + (m.marksObtained || 0), 0);
    const totalMax = studentMarks.reduce((sum, m) => sum + (m.maxMarks || 0), 0);
    const pct = totalMax > 0 ? Math.round((totalObtained / totalMax) * 100) : null;
    // Per-exam breakdown
    const byExam = exams.map(ex => {
      const exMarks = studentMarks.filter(m => m.examId === ex.id);
      if (exMarks.length === 0) return null;
      const obt = exMarks.reduce((s, m) => s + (m.marksObtained || 0), 0);
      const max = exMarks.reduce((s, m) => s + (m.maxMarks || 0), 0);
      return { name: ex.name, pct: max > 0 ? Math.round((obt / max) * 100) : 0, obt, max };
    }).filter(Boolean);
    return { pct, totalObtained, totalMax, byExam };
  };

  const getFeeBalance = (s) => {
    const cls = classes.find(c => c.id === s.classId);
    const className = cls?.name;
    const structure = [...feeStructures]
      .filter(f => (className && f.classId === className) || f.classId === s.classId)
      .filter(f => !academicYear || !f.academicYear || f.academicYear === academicYear)
      .sort((a, b) => (b.academicYear || '').localeCompare(a.academicYear || ''))[0] || null;
    if (!structure) return null;
    const totalDue = Number(structure.totalAmount || 0) + Number(s.previousYearBalance || 0);
    const paid = feePayments.filter(p => p.studentId === s.id && p.type !== 'transportFee' && p.type !== 'admissionFee').reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return totalDue - paid;
  };

  const columns = [
    { label: '#', key: 'num', render: (_, i) => i+1 },
    { label: 'Student', key: 'name',
      searchValue: s => `${s.firstName||''} ${s.lastName||''} ${s.admissionNumber||''}`,
      render: s => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold flex-shrink-0">
            {s.firstName?.[0]}{s.lastName?.[0]}
          </div>
          <div><p className="font-medium">{s.firstName} {s.lastName}</p><p className="">{s.admissionNumber}</p></div>
        </div>
      ), sortable: false },
    { label: 'Class', key: 'class', searchValue: s => getClass(s), render: s => getClass(s) },
    { label: 'Gender', accessor: 'gender', sortable: true },
    { label: 'Parent', key: 'parent', searchValue: s => getParent(s), render: s => getParent(s) },
    { label: 'Phone', accessor: 'phone' },
    { label: 'Fee Balance', key: 'fee', render: s => {
      const bal = getFeeBalance(s);
      if (bal === null) return <span className="text-gray-400">—</span>;
      if (bal <= 0) return <span className="text-green-600 font-semibold">Cleared</span>;
      return <span className="text-red-600 font-semibold">₹{bal.toLocaleString('en-IN')}</span>;
    }},
    { label: 'Attendance', key: 'att', render: s => {
      const a = getAttendanceStats(s.id);
      if (a.pct === null) return <span className="text-gray-400">—</span>;
      const color = a.pct >= 75 ? 'text-green-600' : a.pct >= 50 ? 'text-yellow-600' : 'text-red-600';
      return <span className={`font-semibold ${color}`}>{a.pct}%<span className="text-gray-400 font-normal text-xs ml-1">({a.present}/{a.total}d)</span></span>;
    }},
    { label: 'Exam %', key: 'exam', render: s => {
      const e = getExamPerformance(s.id);
      if (!e || e.pct === null) return <span className="text-gray-400">—</span>;
      const color = e.pct >= 75 ? 'text-green-600' : e.pct >= 50 ? 'text-yellow-600' : 'text-red-600';
      return <span className={`font-semibold ${color}`}>{e.pct}%</span>;
    }},
    { label: 'Status', key: 'status', render: s => <Badge value={s.status} /> },
  ];

  const fields = (
    <div className="grid grid-cols-2 gap-4">
      {[['First Name','firstName','text'],['Last Name','lastName','text'],['Date of Birth','dob','date'],['Phone','phone','tel'],['Email','email','email'],['Roll Number','rollNumber','text'],['Admission Date','admissionDate','date'],['Address','address','text']].map(([label,key,type]) => (
        <div key={key} className={key === 'address' ? 'col-span-2' : ''}>
          <label className="block text-sm font-medium mb-1">{label}</label>
          <input type={type} value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
            className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
        </div>
      ))}
      {[['Gender','gender',['boy','girl']],['Blood Group','bloodGroup',['A+','A-','B+','B-','O+','O-','AB+','AB-']],['Status','status',['active','inactive','transferred','graduated']]].map(([label,key,opts]) => (
        <div key={key}>
          <label className="block text-sm font-medium mb-1">{label}</label>
          <select value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
            className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            {opts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      ))}
      <div>
        <label className="block text-sm font-medium mb-1">Class</label>
        <select value={form.classId||''} onChange={e=>setForm(f=>({...f,classId:e.target.value}))}
          className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">Select Class</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name} - Sec {c.section}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Parent</label>
        <select value={form.parentId||''} onChange={e=>setForm(f=>({...f,parentId:e.target.value}))}
          className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">Select Parent</option>
          {parents.map(p => <option key={p.id} value={p.id}>{p.name} ({p.relation})</option>)}
        </select>
      </div>
    </div>
  );

  const filteredStudents = filterClass === 'all' ? baseStudents : baseStudents.filter(s => s.classId === filterClass);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Students</h1>
          <p className="text-gray-500 text-sm mt-0.5">{filteredStudents.length} of {students.length} students</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            <option value="all">All Classes</option>
            {teacherClasses.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
          </select>
          {!isTeacher && (
            <button onClick={openAdd} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-1 rounded-lg text-sm font-medium transition-colors">
              <Plus size={16} /> Add Student
            </button>
          )}
        </div>
      </div>
      <DataTable columns={columns} data={filteredStudents}
        actions={row => <>
          <button onClick={()=>openView(row)} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600" title="View"><Eye size={15}/></button>
          {!isTeacher && <>
            <button onClick={()=>navigate(`/students/edit/${row.id}`)} className="p-1.5 hover:bg-purple-50 rounded-lg text-purple-600" title="Edit"><Edit2 size={15}/></button>
            <button onClick={()=>remove(row.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500" title="Delete"><Trash2 size={15}/></button>
          </>}
        </>}
      />
      {/* Quick Add Modal */}
      <Modal isOpen={modal==='add'} onClose={()=>setModal(null)} title="New Student Admission" size="xl">
        {fields}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={()=>setModal(null)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-4 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save</button>
        </div>
      </Modal>
      {/* View Modal */}
      <Modal isOpen={modal==='view'} onClose={()=>setModal(null)} title="Student Profile" size="lg">
        {selected && (() => {
          const att = getAttendanceStats(selected.id);
          const exam = getExamPerformance(selected.id);
          const attColor = att.pct === null ? 'text-gray-400' : att.pct >= 75 ? 'text-green-600' : att.pct >= 50 ? 'text-yellow-600' : 'text-red-600';
          const examColor = !exam || exam.pct === null ? 'text-gray-400' : exam.pct >= 75 ? 'text-green-600' : exam.pct >= 50 ? 'text-yellow-600' : 'text-red-600';
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-2xl font-bold">
                  {selected.firstName?.[0]}{selected.lastName?.[0]}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selected.firstName} {selected.lastName}</h3>
                  <p className="text-gray-500">{selected.admissionNumber}</p>
                  <Badge value={selected.status} />
                </div>
              </div>

              {/* Attendance & Exam Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">Attendance</p>
                  <p className={`text-2xl font-bold ${attColor}`}>{att.pct !== null ? `${att.pct}%` : '—'}</p>
                  {att.total > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">{att.present} present / {att.absent} absent / {att.total} days</p>
                  )}
                  {att.total === 0 && <p className="text-xs text-gray-400">No records</p>}
                  {att.pct !== null && (
                    <div className="mt-2 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${att.pct >= 75 ? 'bg-green-500' : att.pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${att.pct}%` }} />
                    </div>
                  )}
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                  <p className="text-xs text-purple-600 font-semibold uppercase tracking-wide mb-1">Overall Exam Performance</p>
                  <p className={`text-2xl font-bold ${examColor}`}>{exam && exam.pct !== null ? `${exam.pct}%` : '—'}</p>
                  {exam && <p className="text-xs text-gray-500 mt-0.5">{exam.totalObtained} / {exam.totalMax} marks</p>}
                  {!exam && <p className="text-xs text-gray-400">No records</p>}
                  {exam && exam.pct !== null && (
                    <div className="mt-2 h-1.5 bg-purple-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${exam.pct >= 75 ? 'bg-green-500' : exam.pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${exam.pct}%` }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Per-exam breakdown */}
              {exam && exam.byExam.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Exam-wise Performance</p>
                  <div className="space-y-1.5">
                    {exam.byExam.map(ex => (
                      <div key={ex.name} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-28 truncate">{ex.name}</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${ex.pct >= 75 ? 'bg-green-500' : ex.pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${ex.pct}%` }} />
                        </div>
                        <span className={`text-xs font-semibold w-10 text-right ${ex.pct >= 75 ? 'text-green-600' : ex.pct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{ex.pct}%</span>
                        <span className="text-xs text-gray-400 w-16 text-right">{ex.obt}/{ex.max}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
                {[
                  ['Class', getClass(selected)], ['Roll No', selected.rollNumber],
                  ['DOB', formatDate(selected.dob)], ['Gender', selected.gender],
                  ['Blood Group', selected.bloodGroup], ['Phone', selected.phone],
                  ['Email', selected.email], ['Admission Date', formatDate(selected.admissionDate)],
                  ['Category', selected.category], ['Religion', selected.religion],
                  ['Aadhar No.', selected.aadharNumber], ['PEN No.', selected.penNumber],
                  ['Father Name', selected.fatherName], ['Father Phone', selected.fatherPhone],
                  ['Father Occupation', selected.fatherOccupation],
                  ['Mother Name', selected.motherName], ['Mother Phone', selected.motherPhone],
                  ['Mother Occupation', selected.motherOccupation],
                  ['City', selected.city], ['District', selected.district],
                  ['State', selected.state], ['PIN Code', selected.pinCode],
                  ['Parent (linked)', getParent(selected)],
                  ['Address', selected.address],
                ].map(([k,v]) => (
                  <div key={k} className={k === 'Address' ? 'col-span-2' : ''}><p className="text-xs text-gray-500 font-medium">{k}</p><p className="text-sm font-medium">{v||'-'}</p></div>
                ))}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
export default StudentList;
